import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { IOrganization } from '@/domain/entities/organization';
import type { IOrganizationMember } from '@/domain/entities/org-member';
import type { IInvitation } from '@/domain/entities/invitation';
import type { IBusinessUser } from '@/domain/entities/user';
import { isOwnerRoleKey, normalizeRoleKey, RoleScopeType } from '@/domain/entities/role';
import { OrgStatus } from '@/domain/shared/shared';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  fetchOrgMemberships,
  fetchOrganization,
  updateOrganizationMember,
  updateOrganization,
} from '@/features/organizations/organizationsThunks';
import {
  selectHasFetchedOrgMembersByOrgId,
  selectOrgMembersByOrgId,
  selectOrgMembersErrorByOrgId,
  selectOrgMembersLoadingByOrgId,
  selectOrganizationById,
  selectOrganizationsError,
  selectOrganizationsLoading,
} from '@/features/organizations/organizationsSlice';
import {
  selectActiveMembershipForCurrentUser,
  selectCurrentUserProfile,
} from '@/store/selectors/authorizationSelectors';
import { selectAllUsers } from '@/features/users/usersSlice';
import {
  selectHasFetchedOrgRolesByOrgId,
  selectOrgRolesByOrgId,
  selectOrgRolesErrorByOrgId,
  selectOrgRolesLoadingByOrgId,
} from '@/features/roles/rolesSlice';
import { fetchRoles, updateRole } from '@/features/roles/rolesThunks';
import {
  createInvitation,
  deleteInvitation,
  fetchInvitations,
  hardDeleteInvitation,
  resendInvitation,
  selectInvitations,
  selectInvitationsFetching,
  selectInvitationsLoading,
} from '@/features/invitations';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { PermissionsEditorModal } from '@/components/admin/PermissionsEditorModal';
import { ErrorNotice } from '@/components/ui/error-notice';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RouteBreadcrumb } from '@/components/route-breadcrumb';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import {
  buildUpdatedOrganizationFromDetailsDraft,
  createOrganizationDetailsDraft,
  getOrganizationSharedFieldErrors,
  type OrganizationDetailsDraft,
} from '@/lib/organization-form';
import { FIXED_ORG_ROLE_KEYS, getFixedOrgRoleAccess, getFixedOrgRoleLabel, isFixedOrgRoleKey } from '@/lib/fixedRoles';
import { Permissions } from '@/lib/permissions';
import { OrganizationDetailsTab } from './components/OrganizationDetailsTab';
import { OrganizationInvitationsTab } from './components/OrganizationInvitationsTab';
import { OrganizationMembersTab } from './components/OrganizationMembersTab';
import { OrganizationRolesTab } from './components/OrganizationRolesTab';

type DraftState = OrganizationDetailsDraft;

type MemberRoleState = {
  roleKey: string;
  permissions: string[];
};

type MemberRoleEditorState = {
  memberKey: string;
  memberUserId: string;
  memberLabel: string;
  assignmentMode: 'fixed' | 'custom';
  fixedRoleKey: string;
  customRoleName: string;
  permissions: string[];
};

function normalizeCapabilities(values: string[]) {
  return Array.from(new Set(values));
}

function areStringListsEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const aSorted = [...a].sort();
  const bSorted = [...b].sort();
  return aSorted.every((value, index) => value === bSorted[index]);
}

function formatDate(timestamp?: number) {
  if (!timestamp) return 'N/A';
  const ms = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getUserDisplayLabel(user?: IBusinessUser | null, fallback?: string) {
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return fullName || user?.email || fallback || 'Unknown user';
}

function normalizeEmail(value?: string | null) {
  return value?.trim().toLowerCase() || '';
}

export default function AdminOrganizationDetailsPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const organization = useAppSelector(selectOrganizationById(organizationId));
  const activeMembership = useAppSelector(selectActiveMembershipForCurrentUser);
  const currentUserProfile = useAppSelector(selectCurrentUserProfile);
  const activeAccountEmail = useAppSelector((state) => state.auth.email);
  const orgMembers = useAppSelector(selectOrgMembersByOrgId(organizationId));
  const orgRoles = useAppSelector(selectOrgRolesByOrgId(organizationId));
  const invitations = useAppSelector(selectInvitations);
  const users = useAppSelector(selectAllUsers);

  const isLoadingOrgMembers = useAppSelector(selectOrgMembersLoadingByOrgId(organizationId));
  const isLoadingOrgRoles = useAppSelector(selectOrgRolesLoadingByOrgId(organizationId));
  const hasFetchedOrgRoles = useAppSelector(selectHasFetchedOrgRolesByOrgId(organizationId));
  const orgMembersError = useAppSelector(selectOrgMembersErrorByOrgId(organizationId));
  const orgRolesError = useAppSelector(selectOrgRolesErrorByOrgId(organizationId));
  const hasFetchedOrgMembers = useAppSelector(selectHasFetchedOrgMembersByOrgId(organizationId));
  const isLoading = useAppSelector(selectOrganizationsLoading);
  const isInvitingMember = useAppSelector(selectInvitationsLoading);
  const isFetchingInvitations = useAppSelector(selectInvitationsFetching);
  const loadError = useAppSelector(selectOrganizationsError);
  const { hasAnyPermission, hasPermission, isSuperAdmin, isSystemUser } = usePermissions();
  const currentMemberId = currentUserProfile?.applicationUserId ?? activeMembership?.memberID ?? null;
  const isViewingActiveOrganization =
    Boolean(organizationId) && Boolean(activeMembership) && activeMembership?.id === organizationId;
  const canReadRequestedOrganization =
    isSuperAdmin || (isViewingActiveOrganization && hasAnyPermission([Permissions.MyOrgRead, Permissions.MyOrgUpdate]));
  const canUpdateRequestedOrganization =
    isSuperAdmin || (isViewingActiveOrganization && hasPermission(Permissions.MyOrgUpdate));
  const canViewMembersTab = isSuperAdmin || hasPermission(Permissions.MemberRead);
  const canViewRolesTab = isSuperAdmin || hasPermission(Permissions.RolesRead);
  const canInviteMembers = isSuperAdmin || (isViewingActiveOrganization && hasPermission(Permissions.MemberInvite));
  const canViewInvitationsTab = canInviteMembers;
  const canEditOrgDetails = canUpdateRequestedOrganization;
  const canEditOrgCapabilities = isSystemUser;
  const canEditOrgStatus = isSystemUser;
  const cameFromOrganizationsList =
    Boolean(
      location.state &&
        typeof location.state === 'object' &&
        'fromOrganizationsList' in location.state &&
        (location.state as { fromOrganizationsList?: boolean }).fromOrganizationsList,
    ) && Boolean(organization);
  const cameFromSettings =
    Boolean(
      location.state &&
        typeof location.state === 'object' &&
        'fromSettings' in location.state &&
        (location.state as { fromSettings?: boolean }).fromSettings,
    ) && Boolean(organization);
  const shouldShowBreadcrumb = cameFromOrganizationsList || cameFromSettings;
  const backPath = cameFromSettings ? '/settings' : isSuperAdmin ? '/admin/organizations' : '/';
  const backButtonLabel = cameFromSettings
    ? 'Back to settings'
    : isSuperAdmin
      ? 'Back to organizations'
      : 'Back to dashboard';

  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isRolePermissionsModalOpen, setIsRolePermissionsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'basic' | 'members' | 'invitations' | 'roles'>('basic');
  const [memberRoleEdits, setMemberRoleEdits] = useState<Record<string, MemberRoleState>>({});
  const [memberRoleEditor, setMemberRoleEditor] = useState<MemberRoleEditorState | null>(null);
  const [isSavingMemberRole, setIsSavingMemberRole] = useState(false);
  const [memberRoleSaveError, setMemberRoleSaveError] = useState<string | null>(null);
  const [hasFetchedInvitations, setHasFetchedInvitations] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const requestedMemberRoleKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!organizationId) return;
    if (organization) return;

    dispatch(fetchOrganization({ orgId: organizationId }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load organization');
      });
  }, [dispatch, organization, organizationId]);

  useEffect(() => {
    if (!organization) return;
    setDraft(createOrganizationDetailsDraft(organization));
  }, [organization]);

  useEffect(() => {
    if (activeTab !== 'members') return;
    if (!canViewMembersTab) return;
    if (!organizationId) return;
    if (hasFetchedOrgMembers || isLoadingOrgMembers) return;

    dispatch(fetchOrgMemberships({ orgId: organizationId }))
      .unwrap()
      .catch(() => {
        // handled by organizationsSlice.orgMembersErrorByOrgId
      });
  }, [activeTab, canViewMembersTab, dispatch, hasFetchedOrgMembers, isLoadingOrgMembers, organizationId]);

  useEffect(() => {
    if (!canViewRolesTab && !canUpdateRequestedOrganization) return;
    if (!organizationId) return;
    if (hasFetchedOrgRoles || isLoadingOrgRoles) return;

    dispatch(fetchRoles({ scopeType: RoleScopeType.ORG, scopeID: organizationId }))
      .unwrap()
      .catch(() => {
        // handled by rolesSlice errorByScope selectors
      });
  }, [
    canUpdateRequestedOrganization,
    canViewRolesTab,
    dispatch,
    hasFetchedOrgRoles,
    isLoadingOrgRoles,
    organizationId,
  ]);

  useEffect(() => {
    if (activeTab === 'members' && !canViewMembersTab) {
      setActiveTab('basic');
    }
    if (activeTab === 'invitations' && !canViewInvitationsTab) {
      setActiveTab('basic');
    }
    if (activeTab === 'roles' && !canViewRolesTab) {
      setActiveTab('basic');
    }
  }, [activeTab, canViewInvitationsTab, canViewMembersTab, canViewRolesTab]);

  useEffect(() => {
    setHasFetchedInvitations(false);
    setInvitationsError(null);
  }, [organizationId]);

  const hasChanges = useMemo(() => {
    if (!organization || !draft) return false;
    const candidate = buildUpdatedOrganizationFromDetailsDraft(organization, draft, {
      canEditStatus: canEditOrgStatus,
      canEditCapabilities: canEditOrgCapabilities,
    });
    const currentCapabilities = normalizeCapabilities(candidate.capabilities ?? []).sort();
    const originalCapabilities = normalizeCapabilities(organization.capabilities ?? []).sort();
    const currentAddress = candidate.address;
    const originalAddress = organization.address;

    return (
      candidate.name !== (organization.name ?? '') ||
      candidate.status !== organization.status ||
      candidate.website !== organization.website ||
      candidate.email !== organization.email ||
      candidate.phone !== organization.phone ||
      (currentAddress?.address ?? undefined) !== (originalAddress?.address ?? undefined) ||
      (currentAddress?.city ?? undefined) !== (originalAddress?.city ?? undefined) ||
      (currentAddress?.state ?? undefined) !== (originalAddress?.state ?? undefined) ||
      (currentAddress?.province ?? undefined) !== (originalAddress?.province ?? undefined) ||
      (currentAddress?.postalCode ?? undefined) !== (originalAddress?.postalCode ?? undefined) ||
      (currentAddress?.country ?? undefined) !== (originalAddress?.country ?? undefined) ||
      (canEditOrgCapabilities && currentCapabilities.join(',') !== originalCapabilities.join(','))
    );
  }, [canEditOrgCapabilities, canEditOrgStatus, draft, organization]);

  const capabilitiesRawValue = useMemo(() => {
    if (!draft) return '';
    return normalizeCapabilities(draft.capabilities).join('\n');
  }, [draft]);
  const sharedFieldErrors = useMemo(() => (draft ? getOrganizationSharedFieldErrors(draft) : {}), [draft]);
  const orgCapabilities = useMemo(() => normalizeCapabilities(draft?.capabilities ?? []), [draft?.capabilities]);
  const orgCapabilitiesSet = useMemo(() => new Set(orgCapabilities), [orgCapabilities]);

  const usersById = useMemo(
    () => new Map<string, IBusinessUser>(users.map((user) => [user.applicationUserId, user])),
    [users],
  );
  const currentUserEmail = useMemo(
    () => normalizeEmail(activeAccountEmail) || normalizeEmail(currentUserProfile?.email),
    [activeAccountEmail, currentUserProfile?.email],
  );
  const ownerUser = organization?.ownerID ? (usersById.get(organization.ownerID) ?? null) : null;
  const createdByLabel = useMemo(
    () => getUserDisplayLabel(ownerUser, organization?.ownerID ?? undefined),
    [organization?.ownerID, ownerUser],
  );
  const orgRolesByLookupKey = useMemo(() => {
    const map = new Map<string, (typeof orgRoles)[number]>();
    for (const role of orgRoles) {
      const key = normalizeRoleKey(role.SK);
      if (key) map.set(key, role);
    }
    return map;
  }, [orgRoles]);
  const fixedRoleOptions = useMemo(() => {
    return FIXED_ORG_ROLE_KEYS.map((key) => ({
      key,
      label: getFixedOrgRoleLabel(key),
    }));
  }, []);

  const seededMemberRolesById = useMemo(() => {
    const byId: Record<string, MemberRoleState> = {};
    for (const member of orgMembers) {
      const roleLookupKey = normalizeRoleKey(member.roleKey);
      if (!roleLookupKey) continue;

      const matchedRole = orgRolesByLookupKey.get(roleLookupKey);
      const fixedRoleAccess = getFixedOrgRoleAccess(roleLookupKey, orgCapabilities);
      byId[member.memberID] = {
        roleKey: roleLookupKey,
        permissions: normalizeCapabilities(fixedRoleAccess?.permissions ?? matchedRole?.permissions ?? []),
      };
    }
    return byId;
  }, [orgCapabilities, orgMembers, orgRolesByLookupKey]);

  const memberRolesById = useMemo(
    () => ({
      ...seededMemberRolesById,
      ...memberRoleEdits,
    }),
    [memberRoleEdits, seededMemberRolesById],
  );
  const orgInvitations = useMemo(() => {
    if (!organizationId) return [];

    return [...invitations]
      .filter((invitation) => invitation.organizationID === organizationId)
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
  }, [invitations, organizationId]);
  const invitationEmails = useMemo(() => {
    return new Set(
      orgInvitations.map((invitation) => normalizeEmail(invitation.email)).filter((email) => email.length > 0),
    );
  }, [orgInvitations]);
  const loadedMemberEmails = useMemo(() => {
    if (!hasFetchedOrgMembers) return new Set<string>();

    return new Set(
      orgMembers
        .map((member) => normalizeEmail(usersById.get(member.memberID)?.email))
        .filter((email) => email.length > 0),
    );
  }, [hasFetchedOrgMembers, orgMembers, usersById]);

  const canSaveMemberRole = useMemo(() => {
    if (!memberRoleEditor) return false;

    const currentRoleState = memberRolesById[memberRoleEditor.memberKey];
    const currentRoleKey = normalizeRoleKey(currentRoleState?.roleKey);
    const hasCurrentRole = Boolean(currentRoleKey);
    const nextRoleKey =
      memberRoleEditor.assignmentMode === 'fixed' ? memberRoleEditor.fixedRoleKey : memberRoleEditor.memberUserId;
    const nextCustomPermissions = normalizeCapabilities(
      memberRoleEditor.permissions.filter((permission) => orgCapabilitiesSet.has(permission)),
    );

    if (!nextRoleKey) return false;

    if (!hasCurrentRole) {
      if (memberRoleEditor.assignmentMode === 'fixed') return true;
      if (nextCustomPermissions.length === 0) return false;
      return Boolean(memberRoleEditor.customRoleName.trim());
    }

    const switchingRoles = currentRoleKey !== nextRoleKey;
    if (switchingRoles) {
      if (memberRoleEditor.assignmentMode === 'custom' && !orgRolesByLookupKey.has(nextRoleKey)) {
        return nextCustomPermissions.length > 0 && Boolean(memberRoleEditor.customRoleName.trim());
      }
      return true;
    }

    const isCurrentCustomRole = currentRoleKey === memberRoleEditor.memberUserId;
    if (!isCurrentCustomRole || memberRoleEditor.assignmentMode !== 'custom') return false;

    const existingCustomRole = orgRolesByLookupKey.get(nextRoleKey);
    const existingRoleName = (existingCustomRole?.name ?? '').trim();
    const nextRoleName = memberRoleEditor.customRoleName.trim();
    if (!nextRoleName) return false;
    if (nextCustomPermissions.length === 0) return false;
    if (nextRoleName !== existingRoleName) return true;

    const existingPermissions = normalizeCapabilities(existingCustomRole?.permissions ?? []);
    const nextPermissions = nextCustomPermissions;
    return !areStringListsEqual(existingPermissions, nextPermissions);
  }, [memberRoleEditor, memberRolesById, orgCapabilitiesSet, orgRolesByLookupKey]);

  const memberRoleSaveButtonLabel = useMemo(() => {
    if (memberRoleEditor?.assignmentMode !== 'custom') return 'Save Role';
    const customRoleKey = memberRoleEditor.memberUserId.trim();
    return customRoleKey && orgRolesByLookupKey.has(customRoleKey) ? 'Update Role' : 'Create Role';
  }, [memberRoleEditor, orgRolesByLookupKey]);

  useEffect(() => {
    setMemberRoleEdits({});
    setMemberRoleEditor(null);
    setMemberRoleSaveError(null);
    setIsRolePermissionsModalOpen(false);
    requestedMemberRoleKeysRef.current.clear();
  }, [organization?.id]);

  useEffect(() => {
    if (activeTab !== 'members') return;
    if (!hasFetchedOrgMembers) return;
    if (!organizationId) return;

    for (const member of orgMembers) {
      const roleLookupKey = normalizeRoleKey(member.roleKey);
      if (!roleLookupKey || isFixedOrgRoleKey(roleLookupKey) || orgRolesByLookupKey.has(roleLookupKey)) continue;

      const roleRequestKey = `${organizationId}::${roleLookupKey}`;
      if (requestedMemberRoleKeysRef.current.has(roleRequestKey)) continue;
      requestedMemberRoleKeysRef.current.add(roleRequestKey);

      dispatch(fetchRoles({ scopeType: RoleScopeType.ORG, scopeID: organizationId, roleKey: roleLookupKey }))
        .unwrap()
        .catch(() => {
          // role fetch errors should not crash members tab rendering
        });
    }
  }, [activeTab, dispatch, hasFetchedOrgMembers, orgMembers, orgRolesByLookupKey, organizationId]);

  useEffect(() => {
    if (activeTab !== 'invitations') return;
    if (!canViewInvitationsTab) return;
    if (!organizationId) return;
    if (hasFetchedInvitations || isFetchingInvitations) return;

    setInvitationsError(null);
    dispatch(fetchInvitations({ organizationID: organizationId }))
      .unwrap()
      .then(() => {
        setHasFetchedInvitations(true);
      })
      .catch((err) => {
        const error = err as { message?: string };
        setInvitationsError(error?.message || 'Failed to load invitations.');
        setHasFetchedInvitations(true);
      });
  }, [activeTab, canViewInvitationsTab, dispatch, hasFetchedInvitations, isFetchingInvitations, organizationId]);

  const save = async (overrideStatus?: OrgStatus) => {
    if (!organization || !draft) return;
    if (!canEditOrgDetails) return;

    setSaveError(null);

    const nextName = draft.name.trim();

    if (!nextName) {
      setSaveError('Organization name is required.');
      return;
    }
    if (sharedFieldErrors.website) {
      setSaveError(sharedFieldErrors.website);
      return;
    }
    if (sharedFieldErrors.email) {
      setSaveError(sharedFieldErrors.email);
      return;
    }

    const updated: IOrganization = {
      ...buildUpdatedOrganizationFromDetailsDraft(
        organization,
        {
          ...draft,
          status: overrideStatus ?? draft.status,
        },
        {
          canEditStatus: canEditOrgStatus,
          canEditCapabilities: canEditOrgCapabilities,
        },
      ),
      updatedAt: Date.now(),
    };

    setSaving(true);
    try {
      const result = await dispatch(updateOrganization({ item: updated })).unwrap();
      const next = result.data ?? updated;
      setDraft(createOrganizationDetailsDraft(next));
      setSaveError(null);
      toast.success('Organization updated.');
    } catch (err) {
      const e = err as { message?: string };
      setSaveError(e?.message || 'Failed to update organization');
      toast.error(e?.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const handleCapabilitiesChange = (permissions: string[]) => {
    if (!canEditOrgCapabilities) return;
    setDraft((state) => (state ? { ...state, capabilities: normalizeCapabilities(permissions) } : state));
  };
  const handleDraftChange = (patch: Partial<DraftState>) => {
    if (!canEditOrgDetails) return;
    setDraft((state) => (state ? { ...state, ...patch } : state));
  };

  const openMemberRoleEditor = (member: IOrganizationMember, memberLabel: string) => {
    if (!canUpdateRequestedOrganization) return;
    if (!isSuperAdmin && member.memberID === currentMemberId) return;

    const current = memberRolesById[member.memberID] ?? {
      roleKey: member.roleKey ?? '',
      permissions: [],
    };
    const normalizedRoleKey = normalizeRoleKey(current.roleKey);
    const hasExistingRole = Boolean(normalizedRoleKey);
    const hasFixedRoles = fixedRoleOptions.length > 0;
    const isCustomRole = normalizedRoleKey === member.memberID;
    const defaultFixedRoleKey = fixedRoleOptions[0]?.key ?? '';
    const effectiveFixedRoleKey =
      hasExistingRole && !isCustomRole && isFixedOrgRoleKey(normalizedRoleKey)
        ? normalizedRoleKey
        : defaultFixedRoleKey;
    const defaultMode: 'fixed' | 'custom' = hasExistingRole
      ? isCustomRole
        ? 'custom'
        : 'fixed'
      : hasFixedRoles
        ? 'fixed'
        : 'custom';
    const existingCustomRoleName = isCustomRole ? (orgRolesByLookupKey.get(normalizedRoleKey)?.name ?? '') : '';
    const defaultCustomRoleName = existingCustomRoleName || `${memberLabel} Role`;

    setMemberRoleSaveError(null);
    setMemberRoleEditor({
      memberKey: member.memberID,
      memberUserId: member.memberID,
      memberLabel,
      assignmentMode: defaultMode,
      fixedRoleKey: effectiveFixedRoleKey,
      customRoleName: defaultCustomRoleName,
      permissions: normalizeCapabilities(current.permissions),
    });
  };

  const handleMemberRolePermissionsChange = (permissions: string[]) => {
    if (!canUpdateRequestedOrganization) return;
    setMemberRoleEditor((state) =>
      state
        ? {
            ...state,
            permissions: normalizeCapabilities(permissions),
          }
        : state,
    );
  };

  const retryOrgMembersLoad = () => {
    if (!organizationId) return;
    if (isLoadingOrgMembers) return;
    dispatch(fetchOrgMemberships({ orgId: organizationId }))
      .unwrap()
      .catch(() => {
        // handled by organizationsSlice.orgMembersErrorByOrgId
      });
  };

  const retryOrgRolesLoad = () => {
    if (!organizationId) return;
    if (isLoadingOrgRoles) return;
    dispatch(fetchRoles({ scopeType: RoleScopeType.ORG, scopeID: organizationId }))
      .unwrap()
      .catch(() => {
        // handled by rolesSlice errorByScope selectors
      });
  };

  const retryInvitationsLoad = () => {
    if (!organizationId) return;
    if (isFetchingInvitations) return;

    setInvitationsError(null);
    dispatch(fetchInvitations({ organizationID: organizationId }))
      .unwrap()
      .then(() => {
        setHasFetchedInvitations(true);
      })
      .catch((err) => {
        const error = err as { message?: string };
        setInvitationsError(error?.message || 'Failed to load invitations.');
        setHasFetchedInvitations(true);
      });
  };

  const handleSaveMemberStatus = async (member: IOrganizationMember, status: OrgStatus) => {
    if (!organizationId) return;
    if (!canUpdateRequestedOrganization) return;
    if (!isSuperAdmin && member.memberID === currentMemberId) return;
    if (member.status === status) return;

    await dispatch(
      updateOrganizationMember({
        orgID: organizationId,
        memberID: member.memberID,
        status,
      }),
    ).unwrap();

    toast.success('Membership status updated.');
  };

  const handleInviteMember = async (email: string) => {
    if (!organizationId) {
      throw new Error('Organization ID is required.');
    }

    const normalizedEmail = normalizeEmail(email);

    if (currentUserEmail && normalizedEmail === currentUserEmail) {
      throw new Error('You cannot invite your active account email.');
    }

    if (hasFetchedOrgMembers && loadedMemberEmails.has(normalizedEmail)) {
      throw new Error('This email already belongs to an organization member.');
    }

    if (invitationEmails.has(normalizedEmail)) {
      throw new Error('This email already has an invitation record for this organization.');
    }

    const result = await dispatch(
      createInvitation({
        email: normalizedEmail,
        organizationID: organizationId,
      }),
    ).unwrap();

    if (result.emailSent) {
      toast.success('Invitation sent.');
    } else if (result.deliveryMessage) {
      toast.warning(result.deliveryMessage);
    } else {
      toast.success('Dry run passed. No invitation was created or sent.');
    }

    return result;
  };

  const handleResendInvitation = async (invitation: IInvitation) => {
    const result = await dispatch(resendInvitation({ invitationId: invitation.id })).unwrap();

    if (result.emailSent) {
      toast.success('Invitation resent.');
    } else {
      toast.warning(result.deliveryMessage || 'Invitation resent, but email delivery was not confirmed.');
    }
  };

  const handleRevokeInvitation = async (invitation: IInvitation) => {
    await dispatch(deleteInvitation({ invitationId: invitation.id })).unwrap();
    toast.success('Invitation revoked.');
  };

  const handleHardDeleteInvitation = async (invitation: IInvitation) => {
    await dispatch(hardDeleteInvitation({ invitationId: invitation.id })).unwrap();
    toast.success('Invitation deleted.');
  };

  const handleSaveMemberRole = async () => {
    if (!memberRoleEditor || !organizationId) return;
    if (!canUpdateRequestedOrganization) return;
    if (!isSuperAdmin && memberRoleEditor.memberUserId === currentMemberId) return;

    const roleKey =
      memberRoleEditor.assignmentMode === 'fixed' ? memberRoleEditor.fixedRoleKey : memberRoleEditor.memberUserId;
    if (!roleKey) {
      setMemberRoleSaveError('Role key is required.');
      return;
    }

    setMemberRoleSaveError(null);
    setIsSavingMemberRole(true);

    try {
      const currentRoleState = memberRolesById[memberRoleEditor.memberKey];
      const currentRoleKey = normalizeRoleKey(currentRoleState?.roleKey);
      const hasCurrentRole = Boolean(currentRoleKey);
      const switchingRoles = currentRoleKey !== roleKey;
      const isCurrentCustomRole = hasCurrentRole && currentRoleKey === memberRoleEditor.memberUserId;

      const fixedRoleAccess =
        memberRoleEditor.assignmentMode === 'fixed' ? getFixedOrgRoleAccess(roleKey, orgCapabilities) : null;
      if (memberRoleEditor.assignmentMode === 'fixed' && !fixedRoleAccess) {
        setMemberRoleSaveError('Selected role is not a supported fixed role.');
        return;
      }

      const sanitizedPermissions =
        memberRoleEditor.assignmentMode === 'fixed'
          ? normalizeCapabilities(fixedRoleAccess?.permissions ?? [])
          : normalizeCapabilities(
              memberRoleEditor.permissions.filter((permission) => orgCapabilitiesSet.has(permission)),
            );
      const removedCount =
        memberRoleEditor.assignmentMode === 'custom'
          ? memberRoleEditor.permissions.length - sanitizedPermissions.length
          : 0;
      const ensureCustomRoleHasPermissions = () => {
        if (sanitizedPermissions.length > 0) return true;
        setMemberRoleSaveError('Select at least one permission for custom roles.');
        return false;
      };

      const refetchOrgRolesIfDeletedRoleMissing = async (
        deletedRoleStatus: 'deleted' | 'not_found' | 'not_applicable',
      ) => {
        if (deletedRoleStatus !== 'not_found') return;

        await dispatch(fetchRoles({ scopeType: RoleScopeType.ORG, scopeID: organizationId }))
          .unwrap()
          .catch(() => {
            // Best effort; roles tab selectors will surface scope errors if any.
          });
      };

      // 1) No current role + fixed selected -> update membership only (roleKey)
      if (!hasCurrentRole && memberRoleEditor.assignmentMode === 'fixed') {
        const membershipUpdateResult = await dispatch(
          updateOrganizationMember({
            orgID: organizationId,
            memberID: memberRoleEditor.memberUserId,
            roleKey,
          }),
        ).unwrap();
        await refetchOrgRolesIfDeletedRoleMissing(membershipUpdateResult.deletedRoleStatus);
      }
      // 2) No current role + custom selected -> create role + update membership (single backend call)
      else if (!hasCurrentRole && memberRoleEditor.assignmentMode === 'custom') {
        const roleName = memberRoleEditor.customRoleName.trim();
        if (!roleName) {
          setMemberRoleSaveError('Role name is required for custom roles.');
          return;
        }
        if (!ensureCustomRoleHasPermissions()) return;
        const membershipUpdateResult = await dispatch(
          updateOrganizationMember({
            orgID: organizationId,
            memberID: memberRoleEditor.memberUserId,
            roleKey,
            createRole: {
              scopeType: RoleScopeType.ORG,
              scopeID: organizationId,
              roleKey,
              name: roleName,
              permissions: sanitizedPermissions,
              isFixed: false,
            },
          }),
        ).unwrap();
        await refetchOrgRolesIfDeletedRoleMissing(membershipUpdateResult.deletedRoleStatus);
      }
      // 3) Has current custom role and staying on custom -> update role only
      else if (!switchingRoles && isCurrentCustomRole && memberRoleEditor.assignmentMode === 'custom') {
        const existingCustomRole = orgRolesByLookupKey.get(roleKey);
        if (!existingCustomRole) {
          setMemberRoleSaveError('Custom role not found in organization scope.');
          return;
        }
        const nextRoleName = memberRoleEditor.customRoleName.trim();
        if (!nextRoleName) {
          setMemberRoleSaveError('Role name is required for custom roles.');
          return;
        }
        if (!ensureCustomRoleHasPermissions()) return;

        await dispatch(
          updateRole({
            scopeType: RoleScopeType.ORG,
            scopeID: organizationId,
            roleKey,
            name: nextRoleName,
            permissions: sanitizedPermissions,
          }),
        ).unwrap();
      }
      // 4) Has role and switching role key -> update membership only
      else if (switchingRoles) {
        const shouldCreateCustomRole =
          memberRoleEditor.assignmentMode === 'custom' && !orgRolesByLookupKey.has(roleKey);

        if (shouldCreateCustomRole) {
          const roleName = memberRoleEditor.customRoleName.trim();
          if (!roleName) {
            setMemberRoleSaveError('Role name is required for custom roles.');
            return;
          }
          if (!ensureCustomRoleHasPermissions()) return;
        }

        const membershipUpdateResult = await dispatch(
          updateOrganizationMember({
            orgID: organizationId,
            memberID: memberRoleEditor.memberUserId,
            roleKey,
            ...(shouldCreateCustomRole
              ? {
                  createRole: {
                    scopeType: RoleScopeType.ORG,
                    scopeID: organizationId,
                    roleKey,
                    name: memberRoleEditor.customRoleName.trim(),
                    permissions: sanitizedPermissions,
                    isFixed: false,
                  },
                }
              : {}),
          }),
        ).unwrap();
        await refetchOrgRolesIfDeletedRoleMissing(membershipUpdateResult.deletedRoleStatus);
      }

      setMemberRoleEdits((previous) => ({
        ...previous,
        [memberRoleEditor.memberKey]: {
          roleKey,
          permissions: sanitizedPermissions,
        },
      }));

      setIsRolePermissionsModalOpen(false);
      setMemberRoleEditor(null);

      if (removedCount > 0) {
        toast.warning(
          `${removedCount} permission${removedCount === 1 ? ' was' : 's were'} removed because they are outside org capabilities.`,
        );
      }
      if (!hasCurrentRole && memberRoleEditor.assignmentMode === 'fixed') {
        toast.success('Fixed role attached.');
      } else if (!hasCurrentRole && memberRoleEditor.assignmentMode === 'custom') {
        toast.success('Custom role created and attached.');
      } else if (!switchingRoles && isCurrentCustomRole && memberRoleEditor.assignmentMode === 'custom') {
        toast.success('Custom role updated.');
      } else if (switchingRoles) {
        toast.success('Membership role switched.');
      } else {
        toast.success('Role assignment saved.');
      }
    } catch (err) {
      const e = err as { message?: string };
      setMemberRoleSaveError(e?.message || 'Failed to save member role assignment.');
    } finally {
      setIsSavingMemberRole(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="space-y-3">
        <ErrorNotice message="Invalid organization id." title="Invalid Request" />
        <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
          {backButtonLabel}
        </Button>
      </div>
    );
  }

  if (!canReadRequestedOrganization) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (!organization && isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size={80} />
      </div>
    );
  }

  if (!organization || !draft) {
    return (
      <div className="space-y-3">
        <ErrorNotice message={loadError || 'Organization not found.'} title="Failed to Load Organization" />
        <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
          {backButtonLabel}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        {shouldShowBreadcrumb ? (
          <RouteBreadcrumb
            items={
              cameFromSettings
                ? [{ label: 'Settings', to: '/settings' }, { label: 'Organization Details' }]
                : [{ label: 'Organizations', to: '/admin/organizations' }, { label: organization.name }]
            }
          />
        ) : null}

        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Organization Details</h1>
            <p className="text-muted-foreground text-sm">Edit organization data, capabilities, and status.</p>
          </div>

          {shouldShowBreadcrumb ? (
            <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
              Back
            </Button>
          ) : null}
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'basic' | 'members' | 'invitations' | 'roles')}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          {canViewMembersTab ? <TabsTrigger value="members">Members</TabsTrigger> : null}
          {canViewInvitationsTab ? <TabsTrigger value="invitations">Invitations</TabsTrigger> : null}
          {canViewRolesTab ? <TabsTrigger value="roles">Roles</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="basic">
          <OrganizationDetailsTab
            organization={organization}
            draft={draft}
            createdByLabel={createdByLabel}
            saving={saving}
            hasChanges={hasChanges}
            canEditDetails={canEditOrgDetails}
            canEditCapabilities={canEditOrgCapabilities}
            canEditStatus={canEditOrgStatus}
            fieldErrors={sharedFieldErrors}
            saveError={saveError}
            capabilitiesRawValue={capabilitiesRawValue}
            onDraftChange={handleDraftChange}
            onOpenCapabilitiesModal={() => {
              if (!canEditOrgCapabilities) return;
              setIsPermissionsModalOpen(true);
            }}
            onSave={() => save()}
            onReset={() => setDraft((s) => (organization && s ? createOrganizationDetailsDraft(organization) : s))}
            onActivate={() => save(OrgStatus.Active)}
            onDeactivate={() => save(OrgStatus.Inactive)}
            formatDate={formatDate}
          />
        </TabsContent>

        {canViewMembersTab ? (
          <TabsContent value="members">
            <OrganizationMembersTab
              members={orgMembers}
              memberRolesById={memberRolesById}
              usersById={usersById}
              currentMemberId={currentMemberId}
              canManageMembers={canUpdateRequestedOrganization}
              canManageOwnMembership={isSuperAdmin}
              canInviteMembers={canInviteMembers}
              organizationName={organization.name}
              isInvitingMember={isInvitingMember}
              onInviteMember={handleInviteMember}
              onManageRole={openMemberRoleEditor}
              onSaveMemberStatus={handleSaveMemberStatus}
              formatDate={formatDate}
              isLoading={isLoadingOrgMembers}
              hasFetched={hasFetchedOrgMembers}
              error={orgMembersError}
              onRetry={retryOrgMembersLoad}
            />
          </TabsContent>
        ) : null}

        {canViewInvitationsTab ? (
          <TabsContent value="invitations">
            <OrganizationInvitationsTab
              invitations={orgInvitations}
              organizationName={organization.name}
              isFetching={isFetchingInvitations}
              isMutating={isInvitingMember}
              hasFetched={hasFetchedInvitations}
              error={invitationsError}
              canManageInvitations={canInviteMembers}
              onRetry={retryInvitationsLoad}
              onInviteMember={handleInviteMember}
              onResend={handleResendInvitation}
              onRevoke={handleRevokeInvitation}
              onDelete={handleHardDeleteInvitation}
              formatDate={formatDate}
            />
          </TabsContent>
        ) : null}

        {canViewRolesTab ? (
          <TabsContent value="roles">
            <OrganizationRolesTab
              roles={orgRoles}
              orgCapabilities={orgCapabilities}
              isLoading={isLoadingOrgRoles}
              hasFetched={hasFetchedOrgRoles}
              error={orgRolesError}
              onRetry={retryOrgRolesLoad}
            />
          </TabsContent>
        ) : null}
      </Tabs>

      <Dialog
        open={Boolean(memberRoleEditor)}
        onOpenChange={(open) => {
          if (!open) {
            setMemberRoleEditor(null);
            setMemberRoleSaveError(null);
            setIsRolePermissionsModalOpen(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Membership Role</DialogTitle>
            <DialogDescription>
              Update role key and permissions for <span className="font-medium">{memberRoleEditor?.memberLabel}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role Assignment</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={memberRoleEditor?.assignmentMode === 'fixed' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() =>
                    setMemberRoleEditor((state) =>
                      state
                        ? {
                            ...state,
                            assignmentMode: 'fixed',
                            fixedRoleKey: state.fixedRoleKey || fixedRoleOptions[0]?.key || '',
                          }
                        : state,
                    )
                  }
                  disabled={fixedRoleOptions.length === 0}
                >
                  Fixed Role
                </Button>
                <Button
                  type="button"
                  variant={memberRoleEditor?.assignmentMode === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() =>
                    setMemberRoleEditor((state) =>
                      state
                        ? (() => {
                            const existingCustomRole = orgRolesByLookupKey.get(state.memberUserId);
                            return {
                              ...state,
                              assignmentMode: 'custom',
                              customRoleName:
                                existingCustomRole?.name?.trim() || state.customRoleName || `${state.memberLabel} Role`,
                              // Switching from fixed -> custom should not inherit fixed role permissions.
                              permissions: normalizeCapabilities(existingCustomRole?.permissions ?? []),
                            };
                          })()
                        : state,
                    )
                  }
                >
                  Custom Role
                </Button>
              </div>
              {fixedRoleOptions.length === 0 ? (
                <p className="text-muted-foreground text-xs">No fixed roles were loaded for this organization yet.</p>
              ) : null}
            </div>

            {memberRoleEditor?.assignmentMode === 'fixed' ? (
              <div className="space-y-2">
                <Label htmlFor="member-fixed-role-key">Fixed Role</Label>
                <Select
                  value={memberRoleEditor?.fixedRoleKey ?? ''}
                  onValueChange={(value) =>
                    setMemberRoleEditor((state) =>
                      state
                        ? {
                            ...state,
                            fixedRoleKey: value,
                          }
                        : state,
                    )
                  }
                >
                  <SelectTrigger id="member-fixed-role-key">
                    <SelectValue placeholder="Select fixed role" />
                  </SelectTrigger>
                  <SelectContent>
                    {fixedRoleOptions.map(({ key, label }) => (
                      <SelectItem key={key} value={key}>
                        {label} ({key})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground text-xs">
                  This attaches one of the organization fixed roles by its role key.
                </p>
              </div>
            ) : null}

            {memberRoleEditor?.assignmentMode === 'custom' ? (
              <div className="space-y-2">
                <Label htmlFor="member-custom-role-name">Custom Role Name</Label>
                <Input
                  id="member-custom-role-name"
                  value={memberRoleEditor?.customRoleName ?? ''}
                  onChange={(event) =>
                    setMemberRoleEditor((state) =>
                      state
                        ? {
                            ...state,
                            customRoleName: event.target.value,
                          }
                        : state,
                    )
                  }
                  placeholder="Enter custom role name"
                />
                <Label>Custom Role Key</Label>
                <Textarea
                  readOnly
                  value={memberRoleEditor?.memberUserId ?? ''}
                  rows={1}
                  className="min-h-10 resize-none overflow-y-auto font-mono text-xs"
                />
                <p className="text-muted-foreground text-xs">Custom role key is always the membership user id.</p>
              </div>
            ) : null}

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Permissions</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRolePermissionsModalOpen(true)}
                  disabled={memberRoleEditor?.assignmentMode === 'fixed'}
                >
                  Edit Permissions
                </Button>
              </div>
              <Textarea
                readOnly
                value={
                  memberRoleEditor?.assignmentMode === 'fixed'
                    ? normalizeCapabilities(
                        getFixedOrgRoleAccess(memberRoleEditor.fixedRoleKey, orgCapabilities)?.permissions ?? [],
                      ).join('\n')
                    : (memberRoleEditor?.permissions ?? []).join('\n')
                }
                rows={7}
                className="max-h-56 min-h-40 overflow-y-auto font-mono text-xs"
                placeholder="No role permissions selected."
              />
              <p className="text-muted-foreground text-xs">
                {memberRoleEditor?.assignmentMode === 'fixed'
                  ? isOwnerRoleKey(memberRoleEditor?.fixedRoleKey)
                    ? 'Owner effectively inherits all organization capabilities.'
                    : 'Viewer receives read-only access for supported organization capabilities.'
                  : 'Only organization capabilities are saved for this role. Other permissions are dimmed in the editor.'}
              </p>
            </div>

            <ErrorNotice message={memberRoleSaveError} title="Failed to Save Member Role" />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMemberRoleEditor(null);
                setMemberRoleSaveError(null);
                setIsRolePermissionsModalOpen(false);
              }}
              disabled={isSavingMemberRole}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveMemberRole} disabled={isSavingMemberRole || !canSaveMemberRole}>
              {isSavingMemberRole ? 'Saving…' : memberRoleSaveButtonLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PermissionsEditorModal
        open={isPermissionsModalOpen}
        onOpenChange={setIsPermissionsModalOpen}
        selectedPermissions={draft.capabilities}
        onPermissionsChange={handleCapabilitiesChange}
        title="Edit Organization Capabilities"
        description="Select which permissions (capabilities) this organization should have."
      />
      <PermissionsEditorModal
        open={isRolePermissionsModalOpen}
        onOpenChange={setIsRolePermissionsModalOpen}
        selectedPermissions={memberRoleEditor?.permissions ?? []}
        onPermissionsChange={handleMemberRolePermissionsChange}
        allowedPermissions={orgCapabilities}
        title="Edit Role Permissions"
        description="Select permissions for this membership role."
      />
    </div>
  );
}
